import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Plan, PlanQuote } from "../../types";
import { useAppConfig } from "../../config/AppConfigContext";
import { bynOf } from "../../utils/api";
import { Icon } from "../common/ui";

/**
 * One tariff (MIXES / CHALLENGE + MIXES / + feedback) with its durations.
 *
 * The plans of a tariff share a `tier`; this card lets the master pick the
 * period, shows what it costs per month and how much the longer period saves,
 * and names the one thing to do next for THIS user — buy, renew, upgrade with
 * the unused days credited, or nothing ("included in your plan").
 * The server computed the quotes; this only renders them.
 */
export const TIER_ORDER = ["mixes", "challenge", "feedback"] as const;

function durationLabel(days: number, t: (k: string, o?: Record<string, unknown>) => string): string {
  if (days >= 360) return t("planCard.durYear");
  const months = Math.round(days / 30);
  return t("planCard.durMonths", { count: months });
}

function money(v: number | string, isRu: boolean, digits = 0): string {
  return Number(v).toLocaleString(isRu ? "ru-RU" : "en-US", { maximumFractionDigits: digits, minimumFractionDigits: 0 });
}

export default function TierCard({
  tier, plans, quotes, allTierPlans, currency, isRu, featured, canPayHere, onBrowserBuy, onLinkPay, onChoose, contact, bynRate,
}: {
  /** RU storefront: show the BYN that bePaid charges, in brackets. */
  bynRate?: number;
  tier: string;
  plans: Plan[];                          // this tier's durations, shortest first
  quotes: Record<string, PlanQuote>;
  allTierPlans: Plan[][];                 // every tier, for the ✓/✗ comparison
  currency: string;
  isRu: boolean;
  featured: boolean;
  canPayHere: boolean;                    // website with PayPal on
  /** Opens the payment-method chooser for this plan. */
  onChoose?: (plan: Plan, quote?: PlanQuote) => void;
  onBrowserBuy?: (plan: Plan) => void;    // inside Telegram
  onLinkPay?: (plan: Plan) => void;       // ready-made payment page (bePaid), admin-confirmed
  contact: string;
}) {
  const { t } = useTranslation();
  const { config } = useAppConfig();
  const [pickedId, setPickedId] = useState(plans[0]?.id);
  const plan = plans.find((p) => p.id === pickedId) ?? plans[0];
  const q = plan ? quotes[plan.id] : undefined;
  const monthly = plans.find((p) => p.days <= 31);

  const blockName = (key: string) => {
    const b = (config.blocks ?? []).find((x) => x.key === key);
    return b ? (isRu ? b.title_ru : b.title_en) : key;
  };
  const included = useMemo(() => new Set((plan?.grants ?? []).map((g) => g.block)), [plan]);
  // Everything any tariff offers, in order: what this one lacks is shown crossed out.
  const allBlocks = useMemo(() => {
    const seen: string[] = [];
    for (const ps of allTierPlans) for (const g of ps[0]?.grants ?? []) if (!seen.includes(g.block)) seen.push(g.block);
    return seen;
  }, [allTierPlans]);

  if (!plan) return null;
  const title = (isRu ? plan.title_ru : plan.title_en) || plan.title_ru;
  const rub = Number(plan.price);
  const months = Math.max(1, Math.round(plan.days / 30));
  const perMonth = plan.days >= 360 ? rub / 12 : rub / months;
  const saving = monthly && plan.id !== monthly.id ? 1 - rub / (Number(monthly.price) * (plan.days >= 360 ? 12 : months)) : 0;
  const isCurrent = !!q?.current_tier && q.current_tier === tier;
  const until = q?.current_until ? new Date(q.current_until).toLocaleDateString(isRu ? "ru-RU" : "en-GB", { day: "numeric", month: "long" }) : null;

  // One button: it opens «Как удобнее оплатить?» (bePaid for Belarus & Russia,
  // PayPal for everyone else). Only when no way to pay exists here at all does
  // the card fall back to writing to the admin.
  const canChoose = !!onChoose && (canPayHere || !!onBrowserBuy || !!onLinkPay);
  let action: React.ReactNode;
  if (q?.kind === "lower") {
    action = <button className="btn btn-glass btn-block plan-card__cta" disabled>{t("planCard.includedInYours")}</button>;
  } else if (canChoose) {
    const label = q?.kind === "upgrade" || q?.kind === "step_up" ? t("planCard.switchTo")
      : q?.kind === "renew" ? t("planCard.renew") : t("planCard.pay");
    action = (
      <>
        <button className="btn btn-primary btn-block plan-card__cta" onClick={() => onChoose!(plan, q)}>
          {(q?.kind === "upgrade" || q?.kind === "step_up") && <Icon name="arrowRight" size={16} />} {label}
        </button>
        <p className="plan-card__methods"><Icon name="lock" size={12} /> {t("planCard.methodsHint")}</p>
      </>
    );
  } else if (contact) {
    action = (
      <button className="btn btn-glass btn-block plan-card__cta" onClick={() => window.open(`https://t.me/${contact}`, "_blank", "noopener,noreferrer")}>
        <Icon name="send" size={16} /> {t("planCard.writeAdmin")}
      </button>
    );
  }

  return (
    <article className={"plan-card tier-card" + (featured ? " is-featured" : "") + (isCurrent ? " is-current" : "")}>
      {isCurrent ? (
        <span className="plan-card__badge is-current">{t("planCard.yourPlan")}{until ? ` · ${t("planCard.until", { date: until })}` : ""}</span>
      ) : featured ? (
        <span className="plan-card__badge">{t("planCard.mostComplete")}</span>
      ) : null}
      <h3 className="plan-card__title">{title}</h3>

      {plans.length > 1 && (
        <div className="tier-durations" role="radiogroup" aria-label={t("planCard.duration")}>
          {plans.map((p) => (
            <button key={p.id} type="button" role="radio" aria-checked={p.id === plan.id}
              className={"tier-duration" + (p.id === plan.id ? " is-on" : "")} onClick={() => setPickedId(p.id)}>
              {durationLabel(p.days, t)}
            </button>
          ))}
        </div>
      )}
      {plans.length === 1 && (
        <p className="plan-card__term"><Icon name="clock" size={14} /> {t("planCard.access", { term: durationLabel(plan.days, t) })}</p>
      )}

      <ul className="plan-card__list">
        {allBlocks.map((b) => included.has(b) ? (
          <li key={b}>
            <span className="plan-card__yes"><Icon name="check" size={13} stroke={2.8} /></span>
            <span>{blockName(b)}</span>
          </li>
        ) : (
          <li key={b} className="is-missing">
            <span className="plan-card__no"><Icon name="x" size={13} stroke={2.6} /></span>
            <span>{blockName(b)}</span>
          </li>
        ))}
      </ul>

      <div className="plan-card__foot">
        <div className="tier-price">
          <p className="plan-card__price">
            <span>{money(rub, isRu)}</span> <small>{currency}</small>
            {bynRate && currency !== "BYN" && <small className="tier-price__alt"> ({money(bynOf(rub, bynRate), isRu)} BYN)</small>}
            <em>{t("planCard.forTerm", { term: durationLabel(plan.days, t) })}</em>
          </p>
          {plan.days > 31 && (
            <p className="tier-price__sub">
              ≈ {money(perMonth, isRu)} {currency} {t("planCard.perMonth")}
              {saving > 0.005 && <span className="tier-save">−{Math.round(saving * 100)}%</span>}
            </p>
          )}
          {q?.kind === "renew" && q.list_price && (
            <p className="tier-credit">
              <Icon name="lock" size={13} /> {t("planCard.priceKept", { price: money(q.due, false, 2), list: money(q.list_price, false, 2) })}
            </p>
          )}
          {q?.kind === "step_up" && (
            <p className="tier-credit is-keep">
              <Icon name="clock" size={13} /> {t("planCard.stepUpKeep")}
            </p>
          )}
          {q?.kind === "upgrade" && Number(q.credit) > 0 && (
            <p className="tier-credit">
              <Icon name="sparkles" size={13} /> {t("planCard.creditLine", { credit: money(q.credit, false, 2) })}
            </p>
          )}
        </div>
        {action}
      </div>
    </article>
  );
}
